import { cn } from 'utils/classNames';
import { objectKeys } from 'utils/collection';

import Icon, { type IconSize, type Props as IconProps } from './Icon';

const PADDINGS = {
	xs: 'p-0.5', // 2
	sm: 'p-1', // 4
	md: 'p-2', // 8
	lg: 'p-3', // 12
} as const;
type IconCirclePadding = keyof typeof PADDINGS;
export const ICON_CIRCLE_PADDINGS = objectKeys(PADDINGS);

const SIZES = {
	sm: {
		xs: 24,
		sm: 28,
		md: 36,
		lg: 44,
	} satisfies Record<IconCirclePadding, IconSize>,
	md: {
		xs: 28,
		sm: 32,
		md: 40,
		lg: 48,
	} satisfies Record<IconCirclePadding, IconSize>,
} as const;
type IconCircleSize = keyof typeof SIZES;
export const ICON_CIRCLE_SIZES = objectKeys(SIZES);

export type IconCircleVariant =
	| 'base'
	| 'branded'
	| 'negative'
	| 'positive'
	| 'subtle'
	| 'transparent';

interface Props extends Omit<IconProps, 'color' | 'size'> {
	padding?: IconCirclePadding;
	size?: IconCircleSize;
	variant: IconCircleVariant;
}

/** Icon with a padded, colored circle background. */
export default function IconCircle({
	className,
	padding = 'xs',
	size = 'sm',
	variant,
	...props
}: Props) {
	return (
		<Icon
			{...props}
			size={SIZES[size][padding]}
			className={cn(
				className,
				'rounded-full',
				variant === 'base' && 'bg-base text-base',
				variant === 'branded' && 'bg-brand text-inverse',
				variant === 'negative' && 'bg-negative text-positive',
				variant === 'positive' && 'bg-positive text-negative',
				variant === 'subtle' && 'bg-subtle text-base',
				variant === 'transparent' && 'bg-positive/80 text-inverse',
				PADDINGS[padding],
			)}
		/>
	);
}
IconCircle.displayName = 'IconCircle';
